import { FC } from "react"

import { useTranslation } from "react-i18next"

import { CmsOpenGraphType, CmsPage, CmsTwitterCard } from "services/cms-seo/type"

import { useBrandSite } from "../BrandSiteContext"
import { Field, FieldRow, inputClass, SectionHeading, ToggleRow } from "../CmsUi"

interface SocialPaneProps {
  page: CmsPage
  onChange: (patch: Partial<CmsPage>) => void
  onPickImage: () => void
}

export const SocialPane: FC<SocialPaneProps> = ({ page, onChange, onPickImage }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const site = useBrandSite()

  const inheritsTitle = page.og_title === null
  const inheritsDescription = page.og_description === null
  const shareTitle = page.og_title ?? page.seo_title
  const shareDescription = page.og_description ?? page.seo_description

  return (
    <div>
      <SectionHeading>{t("cmsSeo.sharePreview", { defaultValue: "Share preview" })}</SectionHeading>

      <div className="mb-6 max-w-[520px] overflow-hidden rounded-xl border border-black/[0.1] dark:border-white/[0.12]">
        <div className="grid aspect-[1.91/1] place-items-center bg-gradient-to-br from-[#1a0b3d] via-[#3b1178] to-[#0e0620] p-5 text-center text-white">
          {page.og_image ? (
            <img src={page.og_image} alt="" className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-xs text-white/60">{t("cmsSeo.noOgImage", { defaultValue: "No Open Graph image set" })}</span>
          )}
        </div>
        <div className="bg-black/[0.03] px-3.5 py-3 dark:bg-white/[0.04]">
          <div className="text-[11.5px] text-gray-500 dark:text-gray-400">{site.host}</div>
          <div className="my-1 text-sm font-semibold text-black dark:text-white">{shareTitle || "—"}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{shareDescription || "—"}</div>
        </div>
      </div>

      <FieldRow>
        <Field label={t("cmsSeo.field.ogTitle", { defaultValue: "Open Graph title" })}>
          <input
            className={inputClass}
            value={page.og_title ?? ""}
            disabled={inheritsTitle}
            placeholder={t("cmsSeo.sameAsTitle", { defaultValue: "Same as SEO title tag" }) as string}
            onChange={(event) => onChange({ og_title: event.target.value })}
          />
        </Field>
      </FieldRow>
      <ToggleRow
        title={t("cmsSeo.sameAsTitle", { defaultValue: "Same as SEO title tag" })}
        checked={inheritsTitle}
        onChange={() => onChange({ og_title: inheritsTitle ? page.seo_title : null })}
      />

      <FieldRow className="mt-5">
        <Field label={t("cmsSeo.field.ogDescription", { defaultValue: "Open Graph description" })}>
          <textarea
            className={inputClass}
            rows={2}
            value={page.og_description ?? ""}
            disabled={inheritsDescription}
            placeholder={t("cmsSeo.sameAsDescription", { defaultValue: "Same as SEO meta description" }) as string}
            onChange={(event) => onChange({ og_description: event.target.value })}
          />
        </Field>
      </FieldRow>
      <ToggleRow
        title={t("cmsSeo.sameAsDescription", { defaultValue: "Same as SEO meta description" })}
        checked={inheritsDescription}
        onChange={() => onChange({ og_description: inheritsDescription ? page.seo_description : null })}
      />

      <SectionHeading>{t("cmsSeo.ogImageHeading", { defaultValue: "Open Graph image" })}</SectionHeading>

      <FieldRow>
        <Field
          label={t("cmsSeo.field.image", { defaultValue: "Image" })}
          hint={t("cmsSeo.ogImageHint", { defaultValue: "1200×630 renders correctly on every network" })}
        >
          <input
            className={inputClass}
            value={page.og_image ?? ""}
            onChange={(event) => onChange({ og_image: event.target.value.trim() || null })}
          />
        </Field>
        <div className="flex-none self-end pb-1">
          <button
            type="button"
            className="btn btn-sm border-black/[0.1] bg-transparent text-black normal-case hover:bg-black/[0.05] dark:border-white/[0.12] dark:text-white dark:hover:bg-white/[0.06]"
            onClick={onPickImage}
          >
            {t("cmsSeo.chooseFromLibrary", { defaultValue: "Choose from library" })}
          </button>
        </div>
      </FieldRow>

      <FieldRow>
        <Field label={t("cmsSeo.field.twitterCard", { defaultValue: "Twitter card type" })}>
          <select
            className={inputClass}
            value={page.twitter_card}
            onChange={(event) => onChange({ twitter_card: event.target.value as CmsTwitterCard })}
          >
            <option value="summary_large_image">summary_large_image</option>
            <option value="summary">summary</option>
          </select>
        </Field>
        <Field label={t("cmsSeo.field.ogType", { defaultValue: "Open Graph type" })}>
          <select
            className={inputClass}
            value={page.og_type}
            onChange={(event) => onChange({ og_type: event.target.value as CmsOpenGraphType })}
          >
            <option value="website">website</option>
            <option value="article">article</option>
          </select>
        </Field>
      </FieldRow>
    </div>
  )
}
